import { Backdrop, CircularProgress, Typography } from "@mui/material";
import axios from "axios";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import routes from "../../../../Common/consts/routes";
import TopBar from "../../../../Layout/components/TopBar/TopBar";
import CreateProjectStepper, {
  StepDetails,
} from "../../components/CreateProjectStepper/CreateProjectStepper";
import useCreateProject from "../../../../Common/hooks/useCreateProject";
import AddOptionalToolsStep from "../AddOptionalToolsStep/AddOptionalToolsStep";
import AddUsersStep from "../AddUsersStep/AddUsersStep";
import EnterDetailsStep from "../EnterDetailsStep/EnterDetailsStep";
import SelectPackageStep from "../SelectPackageStep/SelectPackage";
import { generatePath } from "react-router";
import usePageTitle from "../../../../Common/hooks/usePageTitle";
import { mapDisplayRolesToBackend } from "../../../../Common/utils/roleMapping";
import { mapDbbEnvironmentToFlags } from "../../../../Common/utils/mapDbbEnvironment";
import { User } from "../../../../Common/models/FormModels";
import { selectedPackageAtom } from "../../hooks/useSelectPackage";
import { getPackages } from "../../api/packagesHttpClient";

const stepToPathMap = {
  0: "package",
  1: "tools",
  2: "details",
  3: "users",
};

const CreateProject = () => {
  usePageTitle("Create Project");

  const { activeStep, setActiveStep, advanceAllowed, setAdvanceAllowed, form } =
    useCreateProject();
  const [selectedPackage, setSelectedPackage] = useAtom(selectedPackageAtom);
  const [useCustomRoleMapping, setUseCustomRoleMapping] = useState(false);
  const navigate = useNavigate();

  // Fetch package data to get useCustomRoleMapping
  useEffect(() => {
    if (selectedPackage.id) {
      getPackages()
        .then((packages) => {
          const currentPackage = packages.find(
            (p) => p.id === selectedPackage.id,
          );
          if (currentPackage) {
            setUseCustomRoleMapping(
              currentPackage.useCustomRoleMapping || false,
            );
          }
        })
        .catch(() => {
          // Fallback to false if there's an error
          setUseCustomRoleMapping(false);
        });
    }
  }, [selectedPackage.id]);

  useEffect(() => {
    if (activeStep === 0) {
      setSelectedPackage({ id: "", name: "" });
    }
  }, [activeStep, setSelectedPackage]);

  useEffect(() => {
    return () => {
      setSelectedPackage({ id: "", name: "" });
    };
  }, [setSelectedPackage]);

  useEffect(() => {
    //@ts-ignore
    navigate(`./${stepToPathMap[activeStep]}`);
  }, [activeStep]);
  const {
    trigger,
    formState: { isValid },
  } = form;

  const [isLoading, setIsLoading] = React.useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const stepsDetails: StepDetails[] = [
    {
      name: "Select package",
      component: <SelectPackageStep setActiveStep={setActiveStep} />,
      shouldValidateForm: false,
    },
    {
      name: "Add optional tools",
      component: <AddOptionalToolsStep setAdvanceAllowed={setAdvanceAllowed} />,
      shouldValidateForm: false,
    },
    {
      name: "Enter details",
      component: <EnterDetailsStep form={form} />,
    },
    {
      name: "Add users",
      component: (
        <AddUsersStep form={form} useCustomRoleMapping={useCustomRoleMapping} />
      ),
    },
  ];

  const handleStartLoading = () => {
    setIsLoading(true);
  };

  const handleEndLoading = (
    success = true,
    errorList: any = null,
    projectId?: string,
  ) => {
    setIsLoading(false);
    if (success) {
      const message = projectId ? (
        <div>
          Project creation has been requested!{" "}
          <a
            href={generatePath(routes.projectDetails, {
              id: projectId,
              tab: "details",
            })}
            style={{
              color: "inherit",
              textDecoration: "underline",
              fontWeight: "bold",
            }}
          >
            Go to project
          </a>
        </div>
      ) : (
        "Project creation has been requested!"
      );

      enqueueSnackbar(message, {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      navigate(
        "../" +
          generatePath(routes.projects, {
            tab: "my",
          }),
      );
    }
    if (errorList) {
      Object.entries(errorList).forEach(([key, value]) => {
        const errors = value as string[];
        errors.forEach((error) => {
          enqueueSnackbar(key + ": " + error, {
            variant: "error",
            anchorOrigin: { vertical: "top", horizontal: "center" },
          });
        });
      });
    }
  };

  const handleSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();

    handleStartLoading();

    // Get form data and map display roles to backend roles
    const formData = form.getValues();
    const projectData = {
      ...formData,
      users: formData.users.map((user: User) => ({
        ...user,
        roles: mapDisplayRolesToBackend(user.roles, formData.packageId),
      })),
      tools: formData.tools.map(mapDbbEnvironmentToFlags),
    };

    axios
      .post<{ id: string }>(
        `${window.configuration.apiUrl}api/Projects`,
        projectData,
      )
      .then((response) => {
        handleEndLoading(true, null, response.data?.id);
      })
      .catch((reason) => {
        handleEndLoading(false, reason?.response?.data?.errors);
      });
  };

  const checkKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.code === "Enter") e.preventDefault();
  };
  return (
    <div style={{ height: "100%" }}>
      <TopBar>
        <Typography variant="h4">
          {selectedPackage.name
            ? `Create new ${selectedPackage.name} project`
            : "Create new project"}
        </Typography>
      </TopBar>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <div style={{ height: "calc(100% - 94px)" }}>
        <form
          style={{ height: "100%" }}
          onSubmit={handleSubmit}
          onKeyDown={(e) => checkKeyDown(e)}
        >
          <CreateProjectStepper
            validator={trigger}
            stepsDetails={stepsDetails}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            isFormValid={isValid}
            advanceAllowed={advanceAllowed}
          />
        </form>
      </div>
    </div>
  );
};

export default CreateProject;
